import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { log } from '../lib/logger';
import { X, ChevronRight, ChevronLeft, Sparkles } from 'lucide-react';

/**
 * Role-aware onboarding tour. Spotlights DOM nodes via [data-testid] selectors,
 * shows a floating tooltip beside them, and remembers completion in
 * localStorage per email so we don't nag returning users.
 *
 * Trigger:
 *   - Auto-launched by <Layout /> on first login (see localStorage flag).
 *   - Restart button on /help.
 *   - Any consumer can call `window.dispatchEvent(new CustomEvent('tour:start'))`.
 */

// Every step must reference a data-testid that is already in the sidebar or on
// a page we can navigate to. `route` (optional) is visited before the step shows.
// `roles` filters which roles see the step — omit for "everyone".
const STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to Thrive Ops Hub 👋',
    body: "This is your one-stop replacement for the Excel Ops Hub. Everything you used to open a workbook for now lives here — pipeline, notes, referrals, and reports. Let's take 30 seconds to show you where your day starts.",
    selector: '[data-testid="sidebar"]',
    placement: 'right',
  },
  {
    id: 'dashboard',
    title: 'Dashboard — your morning glance',
    body: "Every time you log in, this tells you exactly where the work is: new referrals waiting, paperwork overdue, pre-auth to submit, and follow-ups to make. Click any tile to open that pipeline segment.",
    selector: '[data-testid="nav-dashboard"]',
    route: '/dashboard',
    placement: 'right',
  },
  {
    id: 'pipeline',
    title: 'Client Pipeline — the full list',
    body: "Every client, from first inquiry to active therapy, in one searchable table. Statuses update automatically based on what's happening (paperwork received, eval scheduled, insurance approved, etc.) — so you're not manually tracking columns anymore.",
    selector: '[data-testid="nav-pipeline"]',
    route: '/pipeline',
    placement: 'right',
  },
  {
    id: 'today',
    title: "Today's Actions — your daily to-do",
    body: "These are the tasks you personally need to move forward today — no more scanning the whole sheet. It re-orders itself as things get done.",
    selector: '[data-testid="nav-today"]',
    route: '/today',
    placement: 'right',
  },
  {
    id: 'pending-notes',
    title: 'Pending Notes',
    body: "See every progress note that's overdue, grouped by clinician with 0-30 / 30-60 / 60+ day aging buckets. This is the report to open when leadership asks who's behind.",
    selector: '[data-testid="nav-pending-notes"]',
    route: '/pending-notes',
    placement: 'right',
  },
  {
    id: 'inquiries',
    title: 'Inquiries — website + fax leads',
    body: "Website form submissions auto-sync from Google every 30 minutes. Fax referrals get logged manually. Work the queue, disposition each lead, and convert the good ones into pipeline clients with one click.",
    selector: '[data-testid="nav-inquiries"]',
    route: '/inquiries',
    placement: 'right',
  },
  {
    id: 'performance',
    title: 'Clinician Performance',
    body: "Show rate, session count, note timeliness, and utilization for every clinician on your roster. OD-only view: this is your team scorecard.",
    selector: '[data-testid="nav-performance"]',
    route: '/performance',
    placement: 'right',
    roles: ['super_user', 'admin', 'hr_payroll', 'office_director'],
  },
  {
    id: 'clinics',
    title: 'Your clinic(s)',
    body: "Everything scoped to a single clinic — active caseload, referrals, staff, notes owed, and clinic-specific KPIs. Office Directors: this is your home base.",
    selector: '[data-testid^="nav-clinic-"]',
    placement: 'right',
  },
  {
    id: 'exec-bi',
    title: 'Executive BI (Super User only)',
    body: "Full financial + operational cube: revenue, realization, contract gaps, OD bonus tracker, denial rates, and the Referrals BI report. Uploaded SimplePractice reports feed this automatically.",
    selector: '[data-testid="nav-executive-bi"]',
    placement: 'right',
    roles: ['super_user'],
  },
  {
    id: 'import',
    title: 'Data Import',
    body: "Every Monday, drop the latest SimplePractice reports (Attendance, Appt Status, Claims, Income, Payer Alloc) here. The natural-key UPSERT keeps history clean — you can re-upload the same file without duplicating rows.",
    selector: '[data-testid="nav-import"]',
    placement: 'right',
    roles: ['super_user', 'admin', 'hr_payroll'],
  },
  {
    id: 'users',
    title: 'User Management (Super User only)',
    body: "When teammates sign in with @thrivespeech.net Google, they land in 'pending'. Come here to approve them and assign role + clinic.",
    selector: '[data-testid="nav-users"]',
    placement: 'right',
    roles: ['super_user'],
  },
  {
    id: 'setup',
    title: 'Setup & Dropdowns',
    body: "Central place for all the dropdown values across the app — clinics, service types, insurance types, referral sources, etc. Billing Admins see just Referral Sources here.",
    selector: '[data-testid="nav-setup"]',
    placement: 'right',
    roles: ['super_user', 'admin', 'hr_payroll'],
  },
  {
    id: 'help',
    title: 'Your safety net',
    body: "Any time you get stuck, click Help & Guide in the sidebar for the full how-to for your role. You can also re-run this tour from there.",
    selector: '[data-testid="nav-help"]',
    route: '/help',
    placement: 'right',
  },
  {
    id: 'finish',
    title: "You're all set 🎉",
    body: "That's the whole hub. If anything looks off or you need a feature, drop a note in Feedback & Bugs — it goes straight to Davinder. Have a great day!",
    selector: '[data-testid="user-name"]',
    placement: 'right',
    isFinal: true,
  },
];

const storageKey = (email) => `thrive_tour_v1_${(email || 'anon').toLowerCase()}`;

export default function OnboardingTour() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [active, setActive] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [rect, setRect] = useState(null);

  // Role-filtered step list
  const steps = useMemo(
    () => STEPS.filter((s) => !s.roles || (user && s.roles.includes(user.role))),
    [user],
  );
  const step = steps[stepIdx];

  // Public start hook — <HelpGuide/> and Layout auto-launch dispatch this.
  useEffect(() => {
    const onStart = () => { setStepIdx(0); setActive(true); };
    window.addEventListener('tour:start', onStart);
    return () => window.removeEventListener('tour:start', onStart);
  }, []);

  // Locate the target element + keep it fresh through scroll/resize.
  // NOTE: `behavior: 'auto'` is critical — 'smooth' scrolls asynchronously, so
  // getBoundingClientRect() would fire with STALE coordinates while the smooth
  // animation was still in progress → spotlight ring landed on the wrong nav
  // item. See the ring-around-Feedback-instead-of-Executive-BI bug report.
  useEffect(() => {
    if (!active || !step) return;
    let cancelled = false;
    let tries = 0;

    // Navigate first if the step requires a different route
    if (step.route && location.pathname !== step.route) {
      navigate(step.route);
    }

    const measure = (el) => {
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };

    const locate = () => {
      if (cancelled) return;
      const el = document.querySelector(step.selector);
      if (el) {
        // Instant scroll so we can measure the settled position on the next frame.
        el.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'center' });
        // Two rAFs: one for the browser to apply the scroll, one for layout to settle.
        requestAnimationFrame(() => requestAnimationFrame(() => {
          if (!cancelled) measure(el);
        }));

        // Re-measure on scroll/resize so the ring stays glued to the target if
        // the user (or another effect) scrolls the page while a step is showing.
        const onWinChange = () => measure(el);
        window.addEventListener('resize', onWinChange);
        window.addEventListener('scroll', onWinChange, true);   // capture=true → inner scrolls
        return () => {
          window.removeEventListener('resize', onWinChange);
          window.removeEventListener('scroll', onWinChange, true);
        };
      }
      tries += 1;
      if (tries < 30) setTimeout(locate, 120);   // ~3.5s of patience
      return undefined;
    };

    // small delay to allow route change + layout paint
    let cleanup;
    const t = setTimeout(() => { cleanup = locate(); }, step.route ? 250 : 60);
    return () => {
      cancelled = true;
      clearTimeout(t);
      if (typeof cleanup === 'function') cleanup();
    };
  }, [active, step, navigate, location.pathname]);

  const close = useCallback((markSeen = false) => {
    setActive(false);
    setRect(null);
    if (markSeen && user?.email) {
      // Safe to swallow — localStorage can throw in Safari private mode or
      // when quota is exceeded. Failure just means the tour re-launches next
      // login, which is a benign fallback.
      try { localStorage.setItem(storageKey(user.email), '1'); }
      catch (err) { log.warn('localStorage unavailable — tour completion not saved:', err); }
    }
  }, [user?.email]);

  if (!active || !step) return null;
  if (!rect) {
    // Backdrop shown even before target locates so it feels responsive.
    return <div className="fixed inset-0 z-[60] bg-[#685B72]/72" data-testid="tour-locating" />;
  }

  // Tooltip position — prefer right of target, wrap to below on narrow screens
  const margin = 16;
  const cardW = 380;
  const cardH = 240;
  let left = rect.left + rect.width + margin;
  let top = rect.top + rect.height / 2 - cardH / 2;
  if (left + cardW > window.innerWidth - 12) {
    left = Math.max(12, rect.left);
    top = rect.top + rect.height + margin;
  }
  if (top < 12) top = 12;
  if (top + cardH > window.innerHeight - 12) top = window.innerHeight - cardH - 12;

  const isFirst = stepIdx === 0;
  const isLast  = stepIdx === steps.length - 1 || step.isFinal;
  const pct = ((stepIdx + 1) / steps.length) * 100;

  return (
    <div className="fixed inset-0 z-[60]" data-testid="onboarding-tour">
      {/* Spotlight cutout via oversized box-shadow */}
      <div
        className="absolute rounded-xl border-2 pointer-events-none transition-all duration-200"
        style={{
          top: rect.top - 6,
          left: rect.left - 6,
          width: rect.width + 12,
          height: rect.height + 12,
          boxShadow: '0 0 0 9999px rgba(20, 14, 30, 0.72)',
          borderColor: '#E8DCF2',
        }}
      />

      {/* Tooltip card */}
      <div
        className="absolute w-[380px] bg-white rounded-2xl shadow-2xl border border-[#E8E5EC] p-5"
        style={{ top, left }}
        data-testid="tour-card"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-[#685B72]" />
            <span className="text-[11px] uppercase tracking-widest text-[#685B72]/70 font-semibold">
              Step {stepIdx + 1} of {steps.length}
            </span>
          </div>
          <button
            onClick={() => close(true)}
            className="text-[#685B72]/60 hover:text-[#52475A] rounded-md p-1 hover:bg-[#F1EDF5]"
            data-testid="tour-close-btn"
            aria-label="Skip tour"
          ><X size={16} /></button>
        </div>

        <h3 className="font-serif text-lg text-[#2D2833] leading-snug mb-1.5">{step.title}</h3>
        <p className="text-sm text-[#685B72] leading-relaxed mb-4">{step.body}</p>

        <div className="h-1 rounded-full bg-[#F1EDF5] mb-4 overflow-hidden">
          <div className="h-full bg-[#685B72] transition-all" style={{ width: `${pct}%` }} />
        </div>

        <div className="flex items-center justify-between gap-2">
          <button
            onClick={() => close(true)}
            className="text-xs text-[#685B72] hover:text-[#52475A] font-medium underline underline-offset-2"
            data-testid="tour-skip-btn"
          >Skip the tour</button>
          <div className="flex gap-2">
            {!isFirst && (
              <button
                onClick={() => setStepIdx((i) => Math.max(0, i - 1))}
                className="text-sm px-3 py-1.5 rounded-lg border border-[#E8E5EC] text-[#52475A] hover:bg-[#F1EDF5] flex items-center gap-1"
                data-testid="tour-prev-btn"
              ><ChevronLeft size={14} /> Back</button>
            )}
            <button
              onClick={() => {
                if (isLast) close(true);
                else setStepIdx((i) => Math.min(steps.length - 1, i + 1));
              }}
              className="text-sm px-3 py-1.5 rounded-lg bg-[#685B72] hover:bg-[#52475A] text-white flex items-center gap-1"
              data-testid={isLast ? "tour-finish-btn" : "tour-next-btn"}
            >{isLast ? 'Got it' : 'Next'} <ChevronRight size={14} /></button>
          </div>
        </div>
      </div>
    </div>
  );
}

export { storageKey };
